/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState } from "react";
import Clarity from "@microsoft/clarity";
import {
  NAVIGATION_EVENTS,
  USER_EVENTS,
  UI_EVENTS,
} from "../../utils/clarityEvents";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Tooltip,
  useTheme,
  Popover,
  Collapse,
  alpha,
  Badge,
} from "@mui/material";
import {
  Menu,
  Build,
  Hub,
  Speed,
  MonitorHeart,
  AdminPanelSettings,
  ExpandLess,
  ExpandMore,
  VisibilityOutlined,
  DarkModeOutlined,
  LightModeOutlined,
  TranslateOutlined,
  SettingsOutlined,
  Close,
  Groups3Rounded as Groups3RoundedIcon,
  CalculateRounded as CalculateRoundedIcon,
  PrecisionManufacturingRounded as PrecisionManufacturingRoundedIcon,
  ReceiptRounded as ReceiptRoundedIcon,
  PriceCheckRounded as PriceCheckRoundedIcon,
  InsightsRounded as InsightsRoundedIcon,
  QueryStatsRounded as QueryStatsRoundedIcon,
  DesignServicesRounded as DesignServicesRoundedIcon,
  SpaceDashboardRounded as SpaceDashboardRoundedIcon,
} from "@mui/icons-material";
import {
  CampaignIcon,
  CompanyIcon,
  HomeIcon,
  MetricsIcon,
  CredentialsIcon,
  UserIcon,
} from "icons/customIcons";
import { SystemHealthIcon } from "../../../src/icons/SystemHealthIcon";
import { useTranslation } from "react-i18next";
import { logout } from "store/slices/session/sessionSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  selectAvatar,
  selectUser,
  selectUserDb,
} from "store/slices/session/sessionSelector";
import {
  selectLanguage,
  selectSidebarCollapsed,
} from "store/slices/app/appSelector";
import { LanguageEnum } from "store/slices/app/appTypes";
import { toggleSidebar } from "store/slices/app/appSlice";
import { getTenantLogo, useTenantBranding } from "../../utils/tenantBranding";
import { useThemeMode } from "../../theme/ThemeModeProvider";
import { BRAND_PILL_INK as ACTIVE_PILL_INK } from "../../theme/theme";
import { useUserRole } from "../../hooks/useUserRole";
import {
  setSimulatedRole,
  clearSimulatedRole,
  setLanguage,
} from "store/slices/app/appSlice";
import { components } from "../../types/api";

type Role = components["schemas"]["Role"];

// Each language is shown in its own name so it's findable whatever the
// current UI language is.
const LANGUAGE_OPTIONS: { value: LanguageEnum; label: string }[] = [
  { value: LanguageEnum.ES, label: "Español" },
  { value: LanguageEnum.EN, label: "English" },
];

const ALL_ROLES: Role[] = [
  "ADMIN",
  "ACCOUNT",
  "CLIENT",
  "TECHNICIAN",
  "PRICING",
  "STRATEGY",
  "COMERCIAL",
];

// The open/closed state of the sidebar's dropdown groups is kept in
// localStorage so it also survives a full page reload.
const ADMIN_MENU_OPEN_KEY = "sidebar.adminMenuOpen";

const readStoredFlag = (key: string): boolean | null => {
  try {
    const value = localStorage.getItem(key);
    return value === null ? null : value === "true";
  } catch {
    return null;
  }
};

const writeStoredFlag = (key: string, value: boolean) => {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // Storage unavailable (private mode, blocked): state just won't persist.
  }
};

// Order and grouping of the main nav items, by path.
const NAV_GROUP_PATHS: string[][] = [
  ["/", "/companies", "/campaigns", "/briefings", "/service-briefings", "/wip"],
  ["/purchase-orders", "/invoices"],
  [
    "/commercial-dashboard",
    "/cost-analysis",
    "/control-board",
    "/client-registry",
    "/commercial-proposals",
    "/users",
  ],
];

// No props needed as we're using Redux
type SidebarProps = Record<string, never>;

const Sidebar: React.FC<SidebarProps> = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const theme = useTheme();
  const { mode, toggleMode } = useThemeMode();
  const currentUser = useSelector(selectUser);
  const avatar = useSelector(selectAvatar);
  const userDb = useSelector(selectUserDb);
  const { role, realRole, isSimulating } = useUserRole();
  const collapsed = useSelector(selectSidebarCollapsed);
  const branding = useTenantBranding();
  // In dark mode the sidebar surface is dark, so the default (dark ink) logo
  // needs to give way to the white variant; hovering previews the opposite
  // mode by showing the other variant on an inverted (black/white) pill.
  const sidebarLogo = getTenantLogo(
    branding,
    mode === "dark" ? "white" : "default"
  );
  const invertedSidebarLogo = getTenantLogo(
    branding,
    mode === "dark" ? "default" : "white"
  );
  const [logoHovered, setLogoHovered] = useState(false);

  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [adminMenuOpen, setAdminMenuOpenState] = useState(
    () => readStoredFlag(ADMIN_MENU_OPEN_KEY) ?? false
  );
  const setAdminMenuOpen = (open: boolean) => {
    setAdminMenuOpenState(open);
    writeStoredFlag(ADMIN_MENU_OPEN_KEY, open);
  };
  const [roleSimMenuOpen, setRoleSimMenuOpen] = useState(false);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const language = useSelector(selectLanguage);
  // The profile Popover opens upward from the profile block. MUI positions a
  // popover by its `top`, so when a section inside it (Language, "Ver como")
  // expands it grows downward and then has to jump back up. Pinning its
  // bottom edge instead (the Popover's `top` is overridden below) makes it
  // grow upward in step with the Collapse animation. 10px gap above the
  // profile block, as before.
  const [userMenuBottom, setUserMenuBottom] = useState(0);

  // Ids of the entity open in the current page, so the breadcrumb's parent
  // crumbs (always generic labels like "View", never the entity's name) can
  // link back to its view page.
  const companyIdFromPath = (() => {
    const match = location.pathname.match(/^\/companies\/([^/]+)(?:\/edit)?$/);
    if (!match || match[1] === "new") return undefined;
    return match[1];
  })();

  // Same for a campaign's view or edit page, and for the internal and merged
  // campaign pages that hang off a parent campaign.
  const campaignIdFromPath = (() => {
    const match = location.pathname.match(/^\/campaigns\/([^/]+)(?:\/edit)?$/);
    if (match && match[1] !== "new") return match[1];
    const nested = location.pathname.match(
      /^\/(?:internal-campaigns|campaign)\/([^/]+)\//
    );
    return nested ? nested[1] : undefined;
  })();

  // And for briefings: the briefing, plus the associated campaign when
  // inside one.
  const briefingPathMatch = location.pathname.match(
    /^\/briefings\/([^/]+)(?:\/associated-campaigns\/([^/]+))?(?:\/edit)?$/
  );
  const briefingIdFromPath =
    briefingPathMatch && briefingPathMatch[1] !== "new"
      ? briefingPathMatch[1]
      : undefined;
  const associatedIdFromPath = briefingPathMatch?.[2];

  // And for a user's view or edit page.
  const userIdFromPath = (() => {
    const match = location.pathname.match(/^\/users\/([^/]+)(?:\/edit)?$/);
    if (!match || match[1] === "new") return undefined;
    return match[1];
  })();

  const handleUserClick = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuBottom(
      window.innerHeight - event.currentTarget.getBoundingClientRect().top + 10
    );
    setUserMenuAnchor(event.currentTarget);
    // Track user profile click event
    Clarity.event(USER_EVENTS.PROFILE_MENU_OPEN);
  };

  const handleCloseUserMenu = () => {
    setUserMenuAnchor(null);
    // Track user menu close event
    Clarity.event(USER_EVENTS.PROFILE_MENU_CLOSE);
  };

  // Returns the breadcrumb trail of sub-pages currently open under a
  // top-level section (e.g. ["View", "Edit"] for "/companies/1/edit"
  // under "/companies"), so the sidebar can show where in that section the
  // user currently is. The last entry is the current page; earlier entries
  // are its ancestors. Returns null when the current path is just the
  // section's root or belongs to another section.
  const getSubBreadcrumb = (
    pathname: string,
    basePath: string
  ): string[] | null => {
    switch (basePath) {
      case "/companies": {
        if (pathname === "/companies/new") return [t("sidebar.sub.new", "New")];
        const companyLabel = t("sidebar.sub.view", "View");
        if (/^\/companies\/[^/]+\/edit$/.test(pathname))
          return [companyLabel, t("sidebar.sub.edit", "Edit")];
        if (/^\/companies\/[^/]+$/.test(pathname)) return [companyLabel];
        return null;
      }
      case "/campaigns": {
        if (pathname === "/campaigns/new") return [t("sidebar.sub.new", "New")];
        if (/insertion-orders\/edit-names$/.test(pathname))
          return [t("sidebar.sub.editNames", "Edit IO Names")];
        const campaignLabel = t("sidebar.sub.view", "View");
        if (/^\/campaigns\/[^/]+\/edit$/.test(pathname))
          return [campaignLabel, t("sidebar.sub.edit", "Edit")];
        if (/^\/campaigns\/[^/]+$/.test(pathname)) return [campaignLabel];
        if (pathname.startsWith("/internal-campaigns/"))
          return [
            campaignLabel,
            t("sidebar.sub.internalCampaign", "Internal Campaign"),
          ];
        if (pathname.startsWith("/campaign/"))
          return [
            campaignLabel,
            t("sidebar.sub.mergedCampaign", "Merged Campaign"),
          ];
        return null;
      }
      case "/metrics": {
        if (pathname.startsWith("/metrics/internal-metrics/"))
          return [t("sidebar.sub.internalMargins", "Internal Margins")];
        if (pathname.startsWith("/metrics/io-margins/"))
          return [t("sidebar.sub.ioMargins", "IO Margins")];
        return null;
      }
      case "/users": {
        if (pathname === "/users/new") return [t("sidebar.sub.new", "New")];
        const userLabel = t("sidebar.sub.view", "View");
        if (/^\/users\/[^/]+\/edit$/.test(pathname))
          return [userLabel, t("sidebar.sub.edit", "Edit")];
        if (/^\/users\/[^/]+$/.test(pathname)) return [userLabel];
        return null;
      }
      case "/purchase-orders": {
        if (pathname === "/purchase-orders/history")
          return [t("sidebar.sub.history", "History")];
        if (/^\/purchase-orders\/[^/]+\/edit$/.test(pathname))
          return [t("sidebar.sub.edit", "Edit")];
        return null;
      }
      case "/invoices": {
        if (pathname === "/invoices/history")
          return [t("sidebar.sub.history", "History")];
        if (/^\/invoices\/[^/]+$/.test(pathname))
          return [t("sidebar.sub.details", "Details")];
        return null;
      }
      case "/commercial-proposals": {
        if (pathname === "/commercial-proposals/new")
          return [t("sidebar.sub.new", "New")];
        if (/^\/commercial-proposals\/[^/]+$/.test(pathname))
          return [t("sidebar.sub.details", "Details")];
        // null y NO un array vacio: el gris de "seccion activa" se decide con
        // Boolean(subBreadcrumb), y Boolean([]) es true, asi que devolver [] dejaba
        // Propuestas sombreada desde cualquier pantalla de la aplicacion.
        return null;
      }
      case "/control-board": {
        if (pathname === "/control-board/business-plan")
          return [t("sidebar.sub.businessPlan", "Business Plan")];
        return null;
      }
      case "/service-briefings": {
        if (pathname === "/service-briefings/new")
          return [t("sidebar.sub.new", "New")];
        if (/^\/service-briefings\/[^/]+\/edit$/.test(pathname))
          return [t("sidebar.sub.edit", "Edit")];
        if (/^\/service-briefings\/[^/]+$/.test(pathname))
          return [t("sidebar.sub.view", "View")];
        return null;
      }
      case "/briefings": {
        if (pathname === "/briefings/new") return [t("sidebar.sub.new", "New")];
        const briefingLabel = t("sidebar.sub.briefView", "Brief view");
        const associatedLabel = t(
          "sidebar.sub.associatedCampaign",
          "Associated campaign"
        );
        if (/associated-campaigns\/[^/]+\/edit$/.test(pathname))
          return [
            briefingLabel,
            associatedLabel,
            t("sidebar.sub.edit", "Edit"),
          ];
        if (/associated-campaigns\/[^/]+$/.test(pathname))
          return [briefingLabel, associatedLabel];
        if (/^\/briefings\/[^/]+\/edit$/.test(pathname))
          return [briefingLabel, t("sidebar.sub.edit", "Edit")];
        if (/^\/briefings\/[^/]+$/.test(pathname)) return [briefingLabel];
        return null;
      }
      default:
        return null;
    }
  };

  const menuItems = [
    {
      text: t("sidebar.home"),
      icon: (
        <HomeIcon
          color={
            location.pathname === "/"
              ? theme.palette.secondary50.main
              : theme.palette.text.primary
          }
        />
      ),
      path: "/",
    },
    {
      text: t("sidebar.company"),
      icon: (
        <CompanyIcon
          color={
            location.pathname === "/companies"
              ? theme.palette.secondary50.main
              : theme.palette.text.primary
          }
        />
      ),
      path: "/companies",
    },
    {
      text: t("sidebar.campaigns"),
      icon: (
        <CampaignIcon
          color={
            location.pathname === "/campaigns" ||
            location.pathname.startsWith("/internal-campaigns/")
              ? theme.palette.secondary50.main
              : theme.palette.text.primary
          }
        />
      ),
      path: "/campaigns",
    },
    ...(role &&
    ["ADMIN", "ACCOUNT", "TECHNICIAN", "PRICING", "STRATEGY"].includes(role)
      ? [
          {
            text: t("sidebar.wip", "WIP"),
            icon: (
              <Speed
                sx={{
                  color:
                    location.pathname === "/wip"
                      ? theme.palette.secondary50.main
                      : theme.palette.text.primary,
                }}
              />
            ),
            path: "/wip",
          },
        ]
      : []),
    ...(role && ["ADMIN", "PRICING", "ACCOUNT"].includes(role)
      ? [
          {
            // Dashboard comercial. ACCOUNT entra pero solo ve el pipeline de
            // propuestas; los comerciales entran como ADMIN.
            text: t("sidebar.commercialDashboard", "Dashboard comercial"),
            icon: (
              <InsightsRoundedIcon
                sx={{
                  color: location.pathname.startsWith("/commercial-dashboard")
                    ? theme.palette.secondary50.main
                    : theme.palette.text.primary,
                }}
              />
            ),
            path: "/commercial-dashboard",
          },
        ]
      : []),
    ...(role && ["ADMIN", "PRICING", "STRATEGY"].includes(role)
      ? [
          {
            // Análisis de costes: coste real frente a venta, para ajustar márgenes.
            // Tiene costes netos de plataforma, así que no lo ven ACCOUNT ni TECHNICIAN.
            text: t("sidebar.costAnalysis", "Análisis de costes"),
            icon: (
              <QueryStatsRoundedIcon
                sx={{
                  color: location.pathname.startsWith("/cost-analysis")
                    ? theme.palette.secondary50.main
                    : theme.palette.text.primary,
                }}
              />
            ),
            path: "/cost-analysis",
          },
        ]
      : []),
    ...(role && ["ADMIN", "PRICING"].includes(role)
      ? [
          {
            // Tablero de control: previsto, Business Plan y real con costes netos de
            // plataforma, así que solo Admin y Pricing.
            text: t("sidebar.controlBoard", "Tablero de control"),
            icon: (
              <SpaceDashboardRoundedIcon
                sx={{
                  color: location.pathname.startsWith("/control-board")
                    ? theme.palette.secondary50.main
                    : theme.palette.text.primary,
                }}
              />
            ),
            path: "/control-board",
          },
        ]
      : []),
    ...(role && ["ADMIN", "STRATEGY", "ACCOUNT"].includes(role)
      ? [
          {
            // El registro de clientes: la fuente del numero de cliente que va dentro
            // del numero de propuesta.
            text: t("sidebar.clientRegistry", "Registro de clientes"),
            icon: (
              <Groups3RoundedIcon
                sx={{
                  color: location.pathname.startsWith("/client-registry")
                    ? theme.palette.secondary50.main
                    : theme.palette.text.primary,
                }}
              />
            ),
            path: "/client-registry",
          },
        ]
      : []),
    ...(role && ["ADMIN", "STRATEGY", "ACCOUNT", "PRICING"].includes(role)
      ? [
          {
            // Propuesta comercial (fase B): la que lleva varios proyectos dentro.
            text: t("sidebar.commercialProposals", "Propuestas"),
            icon: (
              <CalculateRoundedIcon
                sx={{
                  color:
                    location.pathname === "/commercial-proposals"
                      ? theme.palette.secondary50.main
                      : theme.palette.text.primary,
                }}
              />
            ),
            path: "/commercial-proposals",
          },
        ]
      : []),
    ...(role && ["ADMIN", "ACCOUNT", "TECHNICIAN", "PRICING"].includes(role)
      ? [
          {
            text: t("sidebar.briefings", "Briefings"),
            icon: (
              <PrecisionManufacturingRoundedIcon
                sx={{
                  color:
                    location.pathname === "/briefings"
                      ? theme.palette.secondary50.main
                      : theme.palette.text.primary,
                }}
              />
            ),
            path: "/briefings",
          },
        ]
      : []),
    ...(role &&
    ["ADMIN", "STRATEGY", "ACCOUNT", "TECHNICIAN", "PRICING"].includes(role)
      ? [
          {
            // Briefings de House of Stories, House of AI y House of Digital. Mismos roles que
            // el backend: CLIENT no, porque las líneas llevan el coste.
            text: t("sidebar.serviceBriefings", "Briefings de servicios"),
            icon: (
              <DesignServicesRoundedIcon
                sx={{
                  color: location.pathname.startsWith("/service-briefings")
                    ? theme.palette.secondary50.main
                    : theme.palette.text.primary,
                }}
              />
            ),
            path: "/service-briefings",
          },
        ]
      : []),
    ...(role && ["ADMIN", "ACCOUNT", "STRATEGY", "PRICING"].includes(role)
      ? [
          {
            text: t("sidebar.purchaseOrders", "OCs"),
            icon: (
              <ReceiptRoundedIcon
                sx={{
                  color:
                    location.pathname === "/purchase-orders"
                      ? theme.palette.secondary50.main
                      : theme.palette.text.primary,
                }}
              />
            ),
            path: "/purchase-orders",
          },
        ]
      : []),
    ...(role && ["ADMIN", "ACCOUNT", "STRATEGY", "PRICING"].includes(role)
      ? [
          {
            text: t("sidebar.invoices", "Facturas"),
            icon: (
              <PriceCheckRoundedIcon
                sx={{
                  color: location.pathname.startsWith("/invoices")
                    ? theme.palette.secondary50.main
                    : theme.palette.text.primary,
                }}
              />
            ),
            path: "/invoices",
          },
        ]
      : []),
    // Show Users menu item for PRICING here; for ADMIN it lives in the Admin dropdown
    ...(["PRICING"].includes(role || "")
      ? [
          {
            text: t("sidebar.users"),
            icon: (
              <UserIcon
                color={
                  location.pathname === "/users"
                    ? theme.palette.secondary50.main
                    : theme.palette.text.primary
                }
              />
            ),
            path: "/users",
          },
        ]
      : []),
  ];

  // Related items sit together; a divider separates each group. Items the
  // current role can't see are dropped, and so are groups left empty. The
  // Admin dropdown is rendered right after the last group.
  const navGroups = NAV_GROUP_PATHS.map((paths) =>
    paths.flatMap((path) => menuItems.filter((item) => item.path === path))
  ).filter((group) => group.length > 0);

  // Admin-only tooling, tucked into a collapsible group so it doesn't take
  // up permanent vertical space for the everyday nav items above.
  const adminMenuItems =
    role === "ADMIN"
      ? [
          {
            text: t("sidebar.users"),
            icon: (
              <UserIcon
                color={
                  location.pathname === "/users"
                    ? theme.palette.secondary50.main
                    : theme.palette.text.primary
                }
              />
            ),
            path: "/users",
          },
          {
            text: t("sidebar.metrics"),
            icon: (
              <MetricsIcon
                color={
                  location.pathname === "/metrics"
                    ? theme.palette.secondary50.main
                    : theme.palette.text.primary
                }
              />
            ),
            path: "/metrics",
          },
          {
            text: t("sidebar.processor", "Processor"),
            icon: (
              <Build
                sx={{
                  color:
                    location.pathname === "/processor"
                      ? theme.palette.secondary50.main
                      : theme.palette.text.primary,
                }}
              />
            ),
            path: "/processor",
          },
          {
            text: t("sidebar.dataModel", "Data Model"),
            icon: (
              <Hub
                sx={{
                  color:
                    location.pathname === "/data-model"
                      ? theme.palette.secondary50.main
                      : theme.palette.text.primary,
                }}
              />
            ),
            path: "/data-model",
          },
          {
            text: t("sidebar.platformMappings", "Platform Mappings"),
            icon: (
              <Hub
                sx={{
                  color:
                    location.pathname === "/platform-mappings"
                      ? theme.palette.secondary50.main
                      : theme.palette.text.primary,
                }}
              />
            ),
            path: "/platform-mappings",
          },
          {
            text: t("sidebar.api", "API"),
            icon: (
              <Hub
                sx={{
                  color:
                    location.pathname === "/api" ||
                    location.pathname.startsWith("/api/")
                      ? theme.palette.secondary50.main
                      : theme.palette.text.primary,
                }}
              />
            ),
            path: "/api",
          },
          {
            text: t("sidebar.credentials", "Credentials"),
            icon: (
              <CredentialsIcon
                color={
                  location.pathname === "/credentials"
                    ? theme.palette.secondary50.main
                    : theme.palette.text.primary
                }
              />
            ),
            path: "/credentials",
          },
          {
            text: t("sidebar.systemHealth", "System Health"),
            icon: (
              <SystemHealthIcon
                color={
                  location.pathname === "/system-health"
                    ? theme.palette.secondary50.main
                    : theme.palette.text.primary
                }
              />
            ),
            path: "/system-health",
          },
          {
            // Líneas cuyos datos no están al día; lo mismo que el correo diario.
            text: t("sidebar.dataHealth", "Data health"),
            icon: (
              <MonitorHeart
                sx={{
                  color:
                    location.pathname === "/data-health"
                      ? theme.palette.secondary50.main
                      : theme.palette.text.primary,
                }}
              />
            ),
            path: "/data-health",
          },
        ]
      : [];

  const isOnAdminPage = adminMenuItems.some(
    (item) => location.pathname === item.path
  );
  // Landing on an admin page opens the group (and remembers it), but the user
  // can still collapse it from there.
  useEffect(() => {
    if (isOnAdminPage && !adminMenuOpen) setAdminMenuOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnAdminPage]);
  const adminMenuExpanded = adminMenuOpen;

  // Renders one nav item (icon + label + active states + breadcrumb of any
  // sub-page currently open under it). Shared by the main nav list and the
  // collapsible admin group below; `nested` is for the admin group's children,
  // which sit indented next to a guide line, so their icon needs less inset.
  const renderMenuItem = (
    item: {
      text: string;
      icon: React.ReactNode;
      path: string;
    },
    nested = false
  ) => {
    const subBreadcrumb = getSubBreadcrumb(location.pathname, item.path);
    // "Here" is the exact page you're on: gets the solid black highlight.
    // When you're one level deeper (a sub-page), the parent only gets a
    // subtle grey highlight to show the section, while the last breadcrumb
    // row below carries the black "you are here" — any earlier rows are
    // just its ancestors.
    const isHere =
      location.pathname === item.path ||
      (item.path === "/api" && location.pathname.startsWith("/api"));
    const isSectionActive = isHere || Boolean(subBreadcrumb);

    return (
      <React.Fragment key={item.text}>
        <ListItem disablePadding sx={{ width: "100%", ml: !collapsed ? 0 : 0 }}>
          <Tooltip title={collapsed ? item.text : ""} placement="right">
            <ListItemButton
              component={RouterLink}
              to={item.path}
              onClick={() => {
                // Track navigation event based on path. La navegación la
                // gestiona RouterLink (genera un <a href> real), lo que
                // permite click derecho / medio para abrir en otra pestaña.
                if (item.path === "/") {
                  Clarity.event(NAVIGATION_EVENTS.HOME_TAB_CLICK);
                } else if (item.path === "/companies") {
                  Clarity.event(NAVIGATION_EVENTS.COMPANY_TAB_CLICK);
                } else if (item.path === "/campaigns") {
                  Clarity.event(NAVIGATION_EVENTS.CAMPAIGNS_TAB_CLICK);
                } else if (item.path === "/metrics") {
                  Clarity.event(NAVIGATION_EVENTS.METRICS_TAB_CLICK);
                } else if (item.path === "/users") {
                  Clarity.event(NAVIGATION_EVENTS.USERS_TAB_CLICK);
                } else if (item.path === "/system-health") {
                  Clarity.event(NAVIGATION_EVENTS.SYSTEM_HEALTH_TAB_CLICK);
                }
              }}
              sx={{
                borderRadius: 50,
                // Tight vertical spacing: items in a group read as related,
                // and the dividers between groups do the separating.
                margin: "2px 8px",
                padding: "4px",
                display: "flex",
                justifyContent: collapsed ? "center" : "flex-start",
                alignItems: "center",
                textAlign: "center",
                backgroundColor: isHere
                  ? ACTIVE_PILL_INK
                  : isSectionActive
                    ? theme.palette.neutral["200"]
                    : "transparent",
                color: isHere
                  ? theme.palette.secondary50.main
                  : theme.palette.text.primary,
                "&:hover": {
                  backgroundColor: isHere
                    ? ACTIVE_PILL_INK
                    : theme.palette.secondary.main,
                  color: isHere
                    ? theme.palette.secondary50.main
                    : theme.palette.text.primary,
                },
              }}
            >
              <ListItemIcon
                sx={{
                  color: isHere
                    ? theme.palette.secondary50.main
                    : theme.palette.text.primary,
                  minWidth: "unset",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "24px",
                  ml: collapsed ? 0 : nested ? 1 : 2,
                }}
              >
                {item.icon}
              </ListItemIcon>
              {!collapsed && (
                <ListItemText
                  primary={item.text}
                  sx={{
                    fontWeight: 700,
                    textAlign: "left",
                    marginLeft: "8px",
                  }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>
        {subBreadcrumb &&
          !collapsed &&
          subBreadcrumb.map((crumb, crumbIndex) => {
            const isLastCrumb = crumbIndex === subBreadcrumb.length - 1;
            // The entity's "View" crumb (first crumb under "/companies" or
            // "/campaigns") links back to that entity's view page when we're
            // one level deeper (e.g. its edit page), so users can return
            // without the sidebar nav item or browser back button.
            const parentEntityId =
              item.path === "/companies"
                ? companyIdFromPath
                : item.path === "/campaigns"
                  ? campaignIdFromPath
                  : item.path === "/briefings"
                    ? briefingIdFromPath
                    : undefined;
            // Under briefings the second crumb is the associated campaign,
            // which links back to its view page from its edit page.
            const crumbHref = isLastCrumb
              ? undefined
              : crumbIndex === 0 && parentEntityId
                ? `${item.path}/${parentEntityId}`
                : crumbIndex === 1 &&
                    item.path === "/briefings" &&
                    briefingIdFromPath &&
                    associatedIdFromPath
                  ? `/briefings/${briefingIdFromPath}/associated-campaigns/${associatedIdFromPath}`
                  : undefined;
            return (
              <ListItem
                key={`${item.text}-crumb-${crumbIndex}`}
                disablePadding
                sx={{ width: "100%" }}
              >
                <Box
                  sx={{
                    width: "100%",
                    pl: 3 + crumbIndex * 1.5,
                    pr: "8px",
                    mt: crumbIndex === 0 ? "4px" : 0,
                    mb: isLastCrumb ? "8px" : "6px",
                  }}
                >
                  {isLastCrumb ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        // Still a pill on one line, but a rounded box (not a
                        // blob) when a long name wraps.
                        borderRadius: "14px",
                        padding: "4px 12px",
                        backgroundColor: ACTIVE_PILL_INK,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 700,
                          fontFamily: "Nunito",
                          color: theme.palette.secondary50.main,
                          minWidth: 0,
                          lineHeight: 1.35,
                          // Names like "aaaa_bbbb_cccc" have no spaces, so
                          // they must be allowed to break mid-word.
                          overflowWrap: "anywhere",
                        }}
                      >
                        {crumb}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography
                      component={crumbHref ? RouterLink : "p"}
                      to={crumbHref}
                      sx={{
                        display: "inline-block",
                        maxWidth: "100%",
                        fontSize: 12,
                        fontWeight: 600,
                        fontFamily: "Nunito",
                        color: theme.palette.neutral["600"],
                        px: "12px",
                        lineHeight: 1.35,
                        overflowWrap: "anywhere",
                        textDecoration: "none",
                        cursor: crumbHref ? "pointer" : "default",
                        "&:hover": crumbHref
                          ? {
                              color: theme.palette.text.primary,
                              textDecoration: "underline",
                            }
                          : undefined,
                      }}
                    >
                      {crumb}
                    </Typography>
                  )}
                </Box>
              </ListItem>
            );
          })}
      </React.Fragment>
    );
  };

  return (
    <Box
      sx={{
        width: collapsed ? 80 : 250,
        minHeight: "100vh",
        background: theme.palette.background.paper,
        borderRight: `1px solid ${theme.palette.divider}`,
        boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.08)",
        display: "flex",
        flexDirection: "column",
        transition: "width 0.3s ease",
        position: "fixed",
        top: 0,
        left: 0,
        bottom: 0,
        zIndex: 1000,
        alignItems: "center",
        // The nav list scrolls on its own (below), so the logo row and the
        // profile block stay pinned at full size however many groups are open.
        overflow: "hidden",
        // Hide sidebar completely when printing
        "@media print": { display: "none" },
      }}
    >
      {/* Menu Button */}
      <Box
        sx={{
          mt: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          width: "100%",
          flexShrink: 0,
        }}
      >
        {!collapsed && (
          <Tooltip
            title={
              mode === "dark"
                ? t("sidebar.lightMode", "Light mode")
                : t("sidebar.darkMode", "Dark mode")
            }
          >
            <Box
              component="button"
              onClick={toggleMode}
              onMouseEnter={() => setLogoHovered(true)}
              onMouseLeave={() => setLogoHovered(false)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "none",
                cursor: "pointer",
                padding: "4px 10px",
                borderRadius: "50px",
                backgroundColor: logoHovered
                  ? mode === "dark"
                    ? "#FFFFFF"
                    : "#000000"
                  : "transparent",
                transition: "background-color 0.2s ease",
              }}
            >
              <img
                src={logoHovered ? invertedSidebarLogo : sidebarLogo}
                alt={t("sidebar.logoAlt", { name: branding.displayName })}
                height={20}
              />
            </Box>
          </Tooltip>
        )}
        <IconButton
          onClick={() => {
            // Track sidebar toggle event
            Clarity.event(
              collapsed ? UI_EVENTS.SIDEBAR_EXPAND : UI_EVENTS.SIDEBAR_COLLAPSE
            );
            dispatch(toggleSidebar());
          }}
        >
          <Menu />
        </IconButton>
      </Box>

      {/* Menu Items */}
      <List
        sx={{
          flex: "1 1 auto",
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          // Children must keep their natural height and let the list scroll,
          // instead of being squeezed to fit.
          "& > *": { flexShrink: 0 },
        }}
      >
        {navGroups.map((group, groupIndex) => (
          <React.Fragment key={group[0].path}>
            {groupIndex > 0 && (
              <Divider
                component="li"
                sx={{ width: collapsed ? "50%" : "80%", my: 1 }}
              />
            )}
            {group.map((item) => renderMenuItem(item))}
          </React.Fragment>
        ))}

        {adminMenuItems.length > 0 && (
          <>
            {navGroups.length > 0 && (
              <Divider
                component="li"
                sx={{ width: collapsed ? "50%" : "80%", my: 1 }}
              />
            )}
            <ListItem disablePadding sx={{ width: "100%" }}>
              <Tooltip
                title={collapsed ? t("sidebar.adminTools", "Admin") : ""}
                placement="right"
              >
                <ListItemButton
                  onClick={() => setAdminMenuOpen(!adminMenuOpen)}
                  sx={{
                    borderRadius: 50,
                    margin: "2px 8px",
                    padding: "4px",
                    display: "flex",
                    justifyContent: collapsed ? "center" : "flex-start",
                    alignItems: "center",
                    textAlign: "center",
                    color: theme.palette.text.primary,
                    "&:hover": {
                      backgroundColor: theme.palette.secondary.main,
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: theme.palette.text.primary,
                      minWidth: "unset",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: "24px",
                      ml: !collapsed ? 2 : 0,
                    }}
                  >
                    <AdminPanelSettings />
                  </ListItemIcon>
                  {!collapsed && (
                    <>
                      <ListItemText
                        primary={t("sidebar.adminTools", "Admin")}
                        sx={{
                          fontWeight: 700,
                          textAlign: "left",
                          marginLeft: "8px",
                        }}
                      />
                      {adminMenuExpanded ? <ExpandLess /> : <ExpandMore />}
                    </>
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
            <Collapse
              in={adminMenuExpanded}
              timeout="auto"
              unmountOnExit
              sx={{ width: "100%" }}
            >
              {/* Children hang off a vertical guide line that starts under the
                  Admin icon (button margin 8 + padding 4 + icon inset 16 +
                  half the 24px icon = 40px), so they read as its sub-items. */}
              <List
                disablePadding
                sx={
                  collapsed
                    ? { width: "100%" }
                    : {
                        width: "auto",
                        ml: "39px",
                        borderLeft: `2px solid ${theme.palette.divider}`,
                      }
                }
              >
                {adminMenuItems.map((item) => renderMenuItem(item, true))}
              </List>
            </Collapse>
          </>
        )}
      </List>

      <Divider sx={{ width: "80%", flexShrink: 0 }} />

      {/* User Profile */}
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "flex-start",
          padding: 2,
          gap: 2,
          width: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          overflow: "hidden",
          cursor: "pointer", // Makes it clickable
          transition: "background 0.2s ease",
          "&:hover": {
            backgroundColor: theme.palette.neutral[100], // Slight background change on hover
          },
        }}
        onClick={handleUserClick}
      >
        <Tooltip
          title={
            isSimulating
              ? t("sidebar.viewingAsRole", "Viendo como") + `: ${role}`
              : ""
          }
        >
          <Badge
            overlap="circular"
            invisible={!isSimulating}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            badgeContent={
              <VisibilityOutlined
                sx={{
                  fontSize: 12,
                  color: theme.palette.background.paper,
                  backgroundColor: theme.palette.warning.main,
                  borderRadius: "50%",
                  padding: "2px",
                }}
              />
            }
          >
            <Avatar
              src={avatar || "https://via.placeholder.com/36x36"}
              sx={{
                width: 36,
                height: 36,
                flexShrink: 0,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 9999,
              }}
            />
          </Badge>
        </Tooltip>
        {!collapsed && (
          <Box sx={{ minWidth: 0, overflow: "hidden", flex: 1 }}>
            <Typography
              noWrap
              sx={{
                fontSize: 16,
                fontWeight: 700,
                fontFamily: "Nunito",
                color: theme.palette.text.primary,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {userDb
                ? `${userDb.firstName} ${userDb.lastName}`
                : t("sidebar.defaultUserName")}
            </Typography>
            <Typography
              noWrap
              sx={{
                fontSize: 12,
                fontWeight: 400,
                fontFamily: "Nunito",
                color: theme.palette.neutral["600"],
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {currentUser?.email || t("sidebar.noEmail")}
            </Typography>
          </Box>
        )}
      </Box>
      {/* User Menu Modal */}
      <Popover
        open={Boolean(userMenuAnchor)}
        anchorEl={userMenuAnchor}
        onClose={handleCloseUserMenu}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        sx={{ marginLeft: "8px" }}
        slotProps={{
          paper: {
            sx: {
              // MUI sets `top` inline; bottom-pinning needs it overridden.
              top: "auto !important",
              bottom: `${userMenuBottom}px`,
              maxHeight: "calc(100vh - 24px)",
              overflowY: "auto",
            },
          },
        }}
      >
        <Box
          sx={{
            width: "auto",
            p: 1.5,
            background: theme.palette.background.paper,
            boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.08)",
            borderRadius: "8px",
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          {/* User Info */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 1 }}>
            <Avatar
              src={avatar || "https://via.placeholder.com/36x36"}
              sx={{
                width: 36,
                height: 36,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 9999,
              }}
            />
            <Box>
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 700,
                  fontFamily: "Nunito",
                  color: theme.palette.text.primary,
                }}
              >
                {userDb
                  ? `${userDb.firstName} ${userDb.lastName}`
                  : t("sidebar.defaultUserName")}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 400,
                  fontFamily: "Nunito",
                  color: theme.palette.neutral[600],
                }}
              >
                {currentUser?.email || t("sidebar.noEmail")}
              </Typography>
            </Box>
          </Box>

          {isSimulating && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                mx: 1,
                mb: 1,
                px: 1.25,
                py: 0.75,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.warning.main, 0.15),
              }}
            >
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  fontFamily: "Nunito",
                  color: theme.palette.warning.dark,
                }}
              >
                {t("sidebar.viewingAsRole", "Viendo como")}: {role}
              </Typography>
              <IconButton
                size="small"
                onClick={() => dispatch(clearSimulatedRole())}
                sx={{ p: 0.25 }}
              >
                <Close sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          )}

          <Divider />

          {/* Dark / light mode toggle */}
          <ListItemButton
            onClick={toggleMode}
            sx={{
              borderRadius: 2,
              p: 1,
              "&:hover": { backgroundColor: theme.palette.neutral[200] },
              alignItems: "center",
              gap: 1,
            }}
          >
            {mode === "dark" ? (
              <LightModeOutlined
                sx={{ fontSize: 16, color: theme.palette.text.primary }}
              />
            ) : (
              <DarkModeOutlined
                sx={{ fontSize: 16, color: theme.palette.text.primary }}
              />
            )}
            <ListItemText
              primary={
                mode === "dark"
                  ? t("sidebar.lightMode", "Light mode")
                  : t("sidebar.darkMode", "Dark mode")
              }
              sx={{
                fontSize: 14,
                fontFamily: "Nunito",
                fontWeight: 400,
                color: theme.palette.text.primary,
              }}
            />
          </ListItemButton>

          {/* Language: stored in the app slice, applied by useLanguage. */}
          <ListItemButton
            onClick={() => setLanguageMenuOpen((prev) => !prev)}
            sx={{
              borderRadius: 2,
              p: 1,
              "&:hover": { backgroundColor: theme.palette.neutral[200] },
              alignItems: "center",
              gap: 1,
            }}
          >
            <TranslateOutlined
              sx={{ fontSize: 16, color: theme.palette.text.primary }}
            />
            <ListItemText
              primary={t("sidebar.language", "Idioma")}
              sx={{
                fontSize: 14,
                fontFamily: "Nunito",
                fontWeight: 400,
                color: theme.palette.text.primary,
              }}
            />
            {languageMenuOpen ? (
              <ExpandLess sx={{ fontSize: 18 }} />
            ) : (
              <ExpandMore sx={{ fontSize: 18 }} />
            )}
          </ListItemButton>
          <Collapse in={languageMenuOpen} timeout="auto" unmountOnExit>
            <Box sx={{ pl: 1 }}>
              {LANGUAGE_OPTIONS.map(({ value, label }) => (
                <ListItemButton
                  key={value}
                  selected={(language || LanguageEnum.EN) === value}
                  onClick={() => {
                    dispatch(setLanguage(value));
                    setLanguageMenuOpen(false);
                  }}
                  sx={{
                    borderRadius: 2,
                    p: 1,
                    pl: 3,
                    "&:hover": {
                      backgroundColor: theme.palette.neutral[200],
                    },
                    "&.Mui-selected": {
                      backgroundColor: alpha(
                        theme.palette.secondary50.main,
                        0.15
                      ),
                    },
                  }}
                >
                  <ListItemText
                    primary={label}
                    sx={{
                      fontSize: 13,
                      fontFamily: "Nunito",
                      fontWeight: 400,
                      color: theme.palette.text.primary,
                    }}
                  />
                </ListItemButton>
              ))}
            </Box>
          </Collapse>

          {/* Role simulation (admins only): temporarily preview the app as
              another role without touching the real user record. */}
          {realRole === "ADMIN" && (
            <>
              <ListItemButton
                onClick={() => setRoleSimMenuOpen((prev) => !prev)}
                sx={{
                  borderRadius: 2,
                  p: 1,
                  "&:hover": { backgroundColor: theme.palette.neutral[200] },
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <VisibilityOutlined
                  sx={{ fontSize: 16, color: theme.palette.text.primary }}
                />
                <ListItemText
                  primary={t("sidebar.simulateRole", "Ver como (rol)")}
                  sx={{
                    fontSize: 14,
                    fontFamily: "Nunito",
                    fontWeight: 400,
                    color: theme.palette.text.primary,
                  }}
                />
                {roleSimMenuOpen ? (
                  <ExpandLess sx={{ fontSize: 18 }} />
                ) : (
                  <ExpandMore sx={{ fontSize: 18 }} />
                )}
              </ListItemButton>
              <Collapse in={roleSimMenuOpen} timeout="auto" unmountOnExit>
                <Box sx={{ pl: 1 }}>
                  {ALL_ROLES.map((simRole) => (
                    <ListItemButton
                      key={simRole}
                      selected={isSimulating && role === simRole}
                      onClick={() => {
                        dispatch(setSimulatedRole(simRole));
                        setRoleSimMenuOpen(false);
                        handleCloseUserMenu();
                      }}
                      sx={{
                        borderRadius: 2,
                        p: 1,
                        pl: 3,
                        "&:hover": {
                          backgroundColor: theme.palette.neutral[200],
                        },
                        "&.Mui-selected": {
                          backgroundColor: alpha(
                            theme.palette.secondary50.main,
                            0.15
                          ),
                        },
                      }}
                    >
                      <ListItemText
                        primary={simRole}
                        sx={{
                          fontSize: 13,
                          fontFamily: "Nunito",
                          fontWeight: 400,
                          color: theme.palette.text.primary,
                        }}
                      />
                    </ListItemButton>
                  ))}
                  {isSimulating && (
                    <ListItemButton
                      onClick={() => {
                        dispatch(clearSimulatedRole());
                        setRoleSimMenuOpen(false);
                        handleCloseUserMenu();
                      }}
                      sx={{
                        borderRadius: 2,
                        p: 1,
                        pl: 3,
                        "&:hover": {
                          backgroundColor: theme.palette.neutral[200],
                        },
                      }}
                    >
                      <ListItemText
                        primary={t(
                          "sidebar.stopSimulating",
                          "Volver a mi rol real"
                        )}
                        sx={{
                          fontSize: 13,
                          fontFamily: "Nunito",
                          fontWeight: 600,
                          color: theme.palette.error.main,
                        }}
                      />
                    </ListItemButton>
                  )}
                </Box>
              </Collapse>
            </>
          )}

          {/* Menu Options */}
          {[
            // t("sidebar.purchases"),     // Option for managing purchases and billing
            // t("sidebar.groupAccount"), // Option for managing team access and permissions
            // t("sidebar.download"),     // Option for downloading reports and data exports
            t("sidebar.settings"), // Option for user preferences and account settings
          ].map((text, pos: number) => (
            <ListItemButton
              key={text}
              component={RouterLink}
              to="/settings"
              onClick={() => {
                handleCloseUserMenu();
                // Track settings navigation. RouterLink genera un <a href>
                // real para permitir abrir en otra pestaña con click derecho.
                Clarity.event(NAVIGATION_EVENTS.SETTINGS_NAVIGATION);
              }}
              sx={{
                borderRadius: 2,
                p: 1,
                "&:hover": { backgroundColor: theme.palette.neutral[200] },
                alignItems: "center",
                gap: 1,
              }}
            >
              <SettingsOutlined
                sx={{ fontSize: 16, color: theme.palette.text.primary }}
              />
              <ListItemText
                primary={text}
                sx={{
                  fontSize: 14,
                  fontFamily: "Nunito",
                  fontWeight: 400,
                  color: theme.palette.text.primary,
                }}
              />
            </ListItemButton>
          ))}

          {/* Logout Button */}
          <ListItemButton
            sx={{
              borderRadius: 2,
              p: 1,
              backgroundColor: theme.palette.background.paper,
              "&:hover": {
                backgroundColor: alpha(theme.palette.error.main, 0.12),
              },
              alignItems: "center",
              gap: 1,
            }}
            onClick={() => {
              handleCloseUserMenu();
              // Track logout event
              Clarity.event(USER_EVENTS.USER_LOGOUT);
              dispatch(logout());
            }}
          >
            <img
              src={"/icons/logout.png"}
              alt={t("sidebar.logout")}
              height={16}
            />
            <ListItemText
              primary={t("sidebar.logout")}
              sx={{
                fontSize: 14,
                fontFamily: "Nunito",
                fontWeight: 400,
                color: theme.palette.error.main,
              }}
            />
          </ListItemButton>
        </Box>
      </Popover>
    </Box>
  );
};

export default Sidebar;
